// src/features/hosted/components/TryInBrowserCta.tsx
//
// The zero-friction entry: OnBuzz running in the browser on a cloud seat,
// as a guest, with nothing to install and no account. The download is still
// the main funnel for people who are ready to commit; this is for everyone
// who is not yet — a visitor who can be using the product in ~30 seconds
// is worth more than a visitor who bookmarked an installer.
//
// Every click is tagged (cta_click:try_browser_<placement>) so the
// browser → guest → convert funnel can be measured next to the download one.

import React from 'react';
import { Box, Button, Typography } from '@mui/material';
import { Language as BrowserIcon, ArrowForward as ArrowForwardIcon } from '@mui/icons-material';
import { brand } from '../../../config/brand';
import clarityService from '../../../services/clarityService';

interface TryInBrowserCtaProps {
  /** `onDark` = white-on-gradient (hero); `onLight` = themed page section. */
  tone?: 'onDark' | 'onLight';
  /** Where on the page this instance lives — recorded with every click. */
  placement?: string;
  /** `button` = the button alone (header/footer); `block` = button + supporting line. */
  variant?: 'button' | 'block';
  size?: 'small' | 'medium' | 'large';
}

export const TRY_BROWSER_LABEL = 'Try it in your browser';
export const TRY_BROWSER_SUPPORT = 'No install, no account. A cloud workspace opens in about half a minute.';

/** Absolute URL of the hosted app; always opens in a new tab. */
export function hostedChatUrl(): string {
  return brand.links.hostedChat;
}

const TryInBrowserCta: React.FC<TryInBrowserCtaProps> = ({
  tone = 'onDark',
  placement = 'hero',
  variant = 'block',
  size = 'large',
}) => {
  const dark = tone === 'onDark';
  const href = hostedChatUrl();
  const track = () => clarityService.track('cta_click', `try_browser_${placement}`);

  const button = (
    <Button
      variant="contained"
      size={size}
      href={href}
      target="_blank"
      rel="noopener"
      onClick={track}
      startIcon={<BrowserIcon />}
      endIcon={<ArrowForwardIcon />}
      data-testid={`try-browser-${placement}`}
      sx={{
        fontWeight: 800,
        textTransform: 'none',
        px: size === 'large' ? 3.5 : 2.5,
        py: size === 'large' ? 1.5 : undefined,
        borderRadius: 2,
        ...(dark
          ? {
              bgcolor: 'white',
              color: brand.colors.fireDark,
              boxShadow: '0 8px 30px rgba(0,0,0,0.25)',
              '&:hover': { bgcolor: 'rgba(255,255,255,0.92)' },
            }
          : {
              background: brand.gradients.button,
              color: brand.buttonTextColor,
              '&:hover': { background: brand.gradients.buttonHover },
            }),
      }}
    >
      {TRY_BROWSER_LABEL}
    </Button>
  );

  if (variant === 'button') return button;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: { xs: 'center', md: dark ? 'flex-start' : 'center' }, gap: 1 }}>
      {button}
      <Typography
        variant="body2"
        data-testid={`try-browser-${placement}-support`}
        sx={{ color: dark ? 'rgba(255,255,255,0.8)' : 'text.secondary', maxWidth: 420, textAlign: { xs: 'center', md: dark ? 'left' : 'center' } }}
      >
        {TRY_BROWSER_SUPPORT}
      </Typography>
    </Box>
  );
};

export default TryInBrowserCta;
